import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Camera, Utensils, Users, Sparkles, ArrowRight } from 'lucide-react';
import { GALLERY_IMAGES } from '../data/galleryImages';

const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/pfuknvfk_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_18_10%20AM.png';

const FILTERS = [
  { id: 'all', label: 'All', icon: Camera },
  { id: 'fine-dining', label: 'Fine Dining', icon: Utensils },
  { id: 'catering', label: 'Catering', icon: Users },
  { id: 'ambiance', label: 'Ambiance', icon: Sparkles },
];

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredImages =
    activeFilter === 'all'
      ? GALLERY_IMAGES
      : GALLERY_IMAGES.filter((img) => img.category === activeFilter);

  return (
    <main data-testid="gallery-page" className="bg-[#FAF6F0] text-[#1C1917]">
      {/* ============ HERO ============ */}
      <section
        data-testid="gallery-hero"
        className="relative min-h-[70vh] md:min-h-[80vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Top-down feast table spread"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/70 via-[#1C1917]/55 to-[#FAF6F0]/95" />
        </div>

        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-20 py-32">
          <div className="max-w-3xl text-center mx-auto lg:mx-0 lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <span className="h-px w-12 bg-[#C9A227]" />
              <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm md:text-base tracking-[0.4em] uppercase drop-shadow">
                Visual Journey
              </span>
              <span className="h-px w-12 bg-[#C9A227]" />
            </div>

            <h1 className="font-['Marcellus'] text-4xl md:text-6xl lg:text-7xl text-[#FAF6F0] mb-6 leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,0.55)]">
              The Gallery,
              <br />
              <span className="italic font-['Cormorant_Garamond'] text-[#FFE7A8]">moments &amp; mise-en-place.</span>
            </h1>

            <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FAF6F0]/95 max-w-2xl leading-relaxed drop-shadow mx-auto lg:mx-0">
              From plated artistry to private rooms — a quiet curation of evenings spent at Geet.
            </p>
          </div>
        </div>
      </section>

      {/* ============ AWARD BANNER ============ */}
      <section
        data-testid="gallery-award-banner"
        className="relative bg-[#1C1917] text-[#FAF6F0] py-5 md:py-6 overflow-hidden border-y border-[#C9A227]/30"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-5 text-center">
          <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] tracking-[0.4em] uppercase text-xs md:text-sm">
            ✦ Award Winning ✦
          </span>
          <span className="hidden md:block h-5 w-px bg-[#C9A227]/40" />
          <p className="font-['Marcellus'] text-base md:text-xl tracking-wide">
            Voted <span className="text-[#FFE7A8] italic font-['Cormorant_Garamond']">Best Indian Restaurant</span> in South Africa
          </p>
        </div>
      </section>

      {/* ============ FILTER TABS ============ */}
      <section
        data-testid="gallery-filters"
        className="sticky top-20 md:top-24 z-30 bg-[#FAF6F0]/95 backdrop-blur-md border-b border-[#C9A227]/25"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="flex overflow-x-auto py-4 gap-2 md:gap-3 justify-center scrollbar-hide">
            {FILTERS.map((filter) => {
              const active = activeFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  data-testid={`gallery-filter-${filter.id}`}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full whitespace-nowrap transition-all duration-300 border ${
                    active
                      ? 'bg-[#1C1917] text-[#C9A227] border-[#1C1917]'
                      : 'bg-transparent text-[#3F3A30] border-[#C9A227]/30 hover:border-[#C9A227] hover:bg-[#F3EBDE]'
                  }`}
                >
                  <filter.icon size={16} />
                  <span className="font-['Cormorant_Garamond'] text-sm md:text-base tracking-wide">
                    {filter.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ GRID ============ */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#C9A227] opacity-10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#5C4A1F] opacity-10 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredImages.map((image, index) => (
              <button
                key={index}
                data-testid={`gallery-tile-${index}`}
                onClick={() => setSelectedImage(image)}
                className="relative aspect-square overflow-hidden rounded-xl group focus:outline-none focus:ring-2 focus:ring-[#C9A227] cursor-pointer"
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/85 via-[#1C1917]/0 to-[#1C1917]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-x-4 bottom-4 text-left opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  <p className="font-['Cormorant_Garamond'] italic text-[#FFE7A8] text-sm tracking-wider mb-1">
                    ✦ View
                  </p>
                  <p className="font-['Marcellus'] text-[#FAF6F0] text-sm md:text-base line-clamp-2 drop-shadow">
                    {image.alt}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA FOOTER ============ */}
      <section className="relative py-20 md:py-28 bg-[#1C1917] text-[#FAF6F0] overflow-hidden border-t border-[#C9A227]/20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm tracking-[0.35em] uppercase mb-5">
            ✦  Seen Something You Love  ✦
          </p>
          <h2 className="font-['Marcellus'] text-3xl md:text-5xl mb-8 leading-tight">
            Come <span className="italic text-[#FFE7A8]">live the picture.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/geet/contact"
              data-testid="gallery-reserve-btn"
              className="group inline-flex items-center gap-3 bg-[#C9A227] text-[#1C1917] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FAF6F0] transition-all duration-300 shadow-lg"
            >
              Reserve a Table
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/geet/menu"
              className="inline-flex items-center gap-2 border border-[#C9A227]/60 text-[#C9A227] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm rounded-lg hover:bg-[#C9A227]/10 transition-all duration-300"
            >
              View the Menu
            </Link>
          </div>
        </div>
      </section>

      {/* ============ LIGHTBOX ============ */}
      {selectedImage && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-[#1C1917]/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-[#C9A227] hover:text-[#FAF6F0] transition-colors z-10"
            onClick={() => setSelectedImage(null)}
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
};

export default GalleryPage;
